<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex justify-center bg-black/60">
      <div class="m-5 w-full md:w-8/12 lg:w-5/12" @click.self="closeDialog(true)">

        <div class="p-0 bg-gray-50 shadow-xl border border-gray-200 rounded-md text-center">

          <div class="flex flex-col w-full text-black">

            <BlockUI :visible="blocked">

              <div v-if="blocked" class="absolute inset-0 flex items-center justify-center z-50 bg-black/20">
                <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="5" fill="transparent" animationDuration=".5s" />
              </div>

              <div class="flex flex-col w-full rounded-t-md p-3 bg-gradient-to-r from-[#424c42] to-[#647064]">
                <div>
                  <font-awesome-icon :icon="['far', 'fa-envelope']" class="w-6 h-6 text-white" />
                </div>
                <div class="font-semibold text-white text-sm md:text-2xl truncate whitespace-nowrap overflow-hidden text-ellipsis pb-1">
                  {{ $t('menu.mensajes') }}
                </div>

                <div v-if="mensaje.acreditadoDe.id === acreditado.id" class="font-semibold text-white text-sm md:text-2xl truncate whitespace-nowrap overflow-hidden text-ellipsis pb-1 uppercase">
                  {{ mensaje.acreditadoPara.nombre + ' ' + mensaje.acreditadoPara.apellido }}
                </div>
                <div v-else class="font-semibold text-white text-sm md:text-2xl truncate whitespace-nowrap overflow-hidden text-ellipsis pb-1 uppercase">
                  {{ mensaje.acreditadoDe.nombre + ' ' + mensaje.acreditadoDe.apellido }}
                </div>

              </div>


              <div class="container p-4 text-left space-y-3">

                <div id="chatScroll" class="flex-1 max-h-[65vh] overflow-y-auto space-y-3">
                  <div v-for="item in mensajes" :key="item.id">
                    
                    <div class="flex" v-if="item.acreditadoDe.id === acreditado.id">
                      <div class="bg-gray-100 p-3 rounded-lg shadow w-8/12 flex flex-col">
                        <div class="text-sm"> {{ item.acreditadoDe.nombre + ' ' + item.acreditadoDe.apellido }}</div>
                        {{ item.mensaje }}
                        <div class="text-xs"> {{ formatDate(item.fechaAlta, 'dd/mm/yyyy hh:mm:ss') }}</div>
                      </div>
                    </div>
                    <div class="flex justify-end" v-else>
                      <div class="bg-green-500 text-white p-3 rounded-lg shadow w-8/12 flex-col">
                        <div class="text-sm"> {{ item.acreditadoDe.nombre + ' ' + item.acreditadoDe.apellido }}</div>
                        {{ item.mensaje }}
                        <div class="text-xs"> {{ formatDate(item.fechaAlta, 'dd/mm/yyyy hh:mm:ss') }}</div>
                      </div>
                    </div>

                  </div>
                </div>

                <!-- Input -->                 
                <div class="flex gap-2 flex-shrink-0">
                  <Field name="stand" v-slot="{ field, errors }" v-model="respuesta">
                    <TextArea rows="3" v-bind="field" type="text" class="input-base" :placeholder="$t('label.ingrese.mensaje')" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />                    
                  </Field>
                </div>

              </div>


              <div class="px-3 pb-3 flex flex-col md:flex-row gap-2 justify-end w-full bg-gray-100">

                <a @click="responder()" 
                  class="cursor-pointer px-6 text-sm font-bold text-[#424c42] bg-transparent py-3 border border-[#424c42] shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
                  <font-awesome-icon :icon="['fas', 'fa-reply']" /> {{ $t('boton.responder') }}
                </a>
                <a @click="closeDialog(true)"
                  class="cursor-pointer px-6 text-sm font-bold text-red-500 bg-transparent py-3 border border-red-500 shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
                  <font-awesome-icon :icon="['fas', 'fa-xmark']" /> {{ $t('boton.cerrar') }}
                </a>

              </div>
            </BlockUI>


          </div>
        </div>

      </div>
    </div>
  </Teleport>

</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { formatDate } from 'date-fns';

import { Field } from 'vee-validate';
import TextArea from 'primevue/textarea';

import meetingService, { ConfiguracionMeeting } from 'qreventos-suite/meeting';

import Swal from 'sweetalert2'
import BlockUI from 'primevue/blockui';
import ProgressSpinner from 'primevue/progressspinner';

import { Acreditado, MensajeMeeting } from 'qreventos-suite/src/services/suite.interfaces';
import seguridadServices from '@/services/seguridad.services';
import configuracionMeetingService from '@/services/configuracion.merting.services';

export default defineComponent({
  components: {
    BlockUI, ProgressSpinner, Field, TextArea
  },
  props: {
    closeFunction: {
      type: Function,
      required: false
    },
    mensaje: {
      type: {} as PropType<MensajeMeeting>,
      required: true,
      deep: true
    },
    acreditado: {
      type: {} as PropType<Acreditado>,
      required: true
    },
  },
  data() {
    return {
      blocked: false,
      respuesta: '',
      mensajes: [] as MensajeMeeting[],
    };
  },
  watch: {
    mensaje: {
      handler(nuevo) {
        if (this.mensaje) {
          this.fillMensajesBy(this.mensaje);
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    formatDate,
    async fillMensajesBy(mensaje: MensajeMeeting) {
      try {
        this.blocked = true;
        const hash: string = seguridadServices.getLocalHash() as string;
        const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

        var idAcreditado = this.mensaje.acreditadoPara.id;
        if (this.mensaje.acreditadoPara.id === this.acreditado.id) {
          idAcreditado = this.mensaje.acreditadoDe.id;
        }

        this.mensajes = await meetingService.findConversacionBy(configuracionMeeting.evento.id, hash, idAcreditado);
      }
      catch (error) {
        this.blocked = false;
        Swal.fire({ title: "Error", text: 'Error [' + error + ']', icon: "error" });
      } finally {
        this.blocked = false;
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = document.getElementById('chatScroll');
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },    
    closeDialog(reload: boolean = false) {
      if (this.closeFunction) {
        this.mensajes = [] as MensajeMeeting[];
        this.closeFunction(reload);
      }
    },
    async responder() {
      try {
        this.blocked = true;

        if(!this.respuesta || this.respuesta.trim() === '') {
          Swal.fire({ title: "Error", html: this.$t('errors.ingrese.mensaje'), icon: "error" });
          return;
        }

        const hash: string = seguridadServices.getLocalHash() as string;
        const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

        var idAcreditado = this.mensaje.acreditadoPara.id;
        if (this.mensaje.acreditadoPara.id === this.acreditado.id) {
          idAcreditado = this.mensaje.acreditadoDe.id;
        }

        await meetingService.responder(configuracionMeeting.evento.id, hash, idAcreditado, this.respuesta);
        
        this.closeDialog(true);
      }
      catch (error) {
        this.blocked = false;
        Swal.fire({ title: "Error", text: 'Error [' + error + ']', icon: "error" });
      } finally {
        this.blocked = false;
      }
    },
  },
  async mounted() {
    document.body.style.overflow = "hidden";
    this.scrollToBottom();
  },
  unmounted() {
    document.body.style.overflow = "";
  }
});
</script>